<template>
  <main>
    <article class="row">
      <section class="col s4">
        <help />
      </section>
      <section class="col s4">
        <h4><strong>Ratings</strong></h4>
      </section>
      <autocomplete id="home-analyst-autocomplete"
                    name="Analyst Search"
                    size="s4"
                    :picks="$store.getters.analystsAutocompleteData"
                    watermark="Name/Email"
                    :disabled="false"
                    @picked="handleAnalystAutocomplete"
                    v-if="$store.getters.analystsAutocompleteData.length && $auth.hasRoles('analyst', 'adp')"
      />
    </article>

    <article class="table-wrapper">
      <article class="row">
        <look-ahead id="ratings-quotes-lookahead" name="Add Ticker" size="s4" watermark="MSFT or Microsoft"
          :picks="instrumentPicks" @searched="fetchInstruments" @picked="added" />
        <section class="col s4" />
        <section class="col s4 left-align">
          <div class="right">
            <h6 >
              Points Allocated:&nbsp;
              <span :class="totalPoints <= 100 ? 'teal-text' : 'pink-text'">
                {{ totalPoints }}
              </span>
              <span class="grey-text small"> out of 100</span>
            </h6>
            <h6>
              Total Stocks:&nbsp;
              <span class="teal-text">{{ ratings.length }}</span>
            </h6>
          </div>
        </section>
      </article>
      <table>
        <thead>
          <tr >
            <th class="clickable" @click="sortRatings('instrument.company.name')">Stock</th>
            <th class="clickable" @click="sortRatings('points')">Rating</th>
            <th class="clickable" @click="sortRatings('instrument.company.sector.name')">Sector</th>
            <th class="clickable" @click="sortRatings('updated')">Freshness</th>
            <th class="" />
          </tr>
        </thead>
        <tbody>
          <tr :class="rating.instrument.id === mostRecentlyAdded ? 'yellow accent-1' : ''" v-for="rating of ratings" :key="rating.id">
            <td>
              <ul class="collection">
                <li class="collection-item avatar">
                  <router-link :to="`/company?company_id=${rating.instrument.company.id}`"
                               class="blue-grey-text text-darken-4"
                  >
                    <img :src="`https://g.foolcdn.com/art/companylogos/mark/${rating.instrument.symbol.toUpperCase()}.png`"
                         alt="logo"
                         class="circle"
                    >
                    <span class="title">
                      <strong>{{ rating.instrument.exchange.quotes_symbol }}:{{ rating.instrument.symbol }}</strong>
                    </span>

                    <p>
                      {{ rating.instrument.company.name | pretty }}
                    </p>
                  </router-link>
                </li>
              </ul>
            </td>
            <td>
              <input placeholder="0"
                     type="number"
                     class="validate"
                     min="-100"
                     max="100"
                     :value="rating.points"
                     @input="adjustedRating(rating, $event)"
                     @blur="updated(rating, $event)"
              >
            </td>
            <td>
              <span style="position: relative; top: 10px;">
                {{ rating.instrument.company.sector.name }}
              </span>
              <p class="grey-text">
                <img :src="`https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.1.0/flags/4x3/${rating.instrument.company.country.code.toLowerCase()}.svg`"
                      height="16"
                      :title="rating.instrument.company.country.name"
                >
              </p>
            </td>
            <td
                :class="new Date() - new Date(rating.updated) > 7776000000 ? 'red-text text-accent-3 strong': ''"
            >
              {{ new Date() - new Date(rating.updated) | ago }}
            </td>
            <td>
              <a class="btn-small waves-effect waves-light blue"
                 title="Confirm"
                 style="position: relative; top: 10px;"
                 @click.prevent="confirmed(rating)"
              >
                <i class="fas fa-check-square" />
              </a>
              &nbsp;
              <a class="btn-small waves-effect waves-light pink"
                 title="Delete"
                 style="position: relative; top: 10px;"
                 @click.prevent="deleted(rating)"
              >
                <i class="fas fa-trash-alt" />
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </article>
    <article>
      <p class="right-align" style="padding-right: 10px;">
        <a :href="`https://app.periscopedata.com/app/fool/${$auth.hasRoles('farm-team') ? '219787/IQ-Farmteam' : '96619/IQ-Analyst'}`">
          Periscope
        </a>
      </p>
    </article>
    <article id="ratings-usm-temp-modal" class="modal">
      <section class="modal-content">
        <h4>What is the ticker's <span class="amber-text strong">Security Class</span>?</h4>
        <article class="security-class-wrapper" style="display: flex; justify-content: center;">
          <section class="left-align">
            <p v-for="securityClass of securityClasses" :key="securityClass.id">
              <label>
                <input name="security-class" type="radio" :value="securityClass.id" v-model="selectedSecurityClass">
                <span>{{ securityClass.name }}</span>
              </label>
            </p>
          </section>
        </article>
      </section>
      <section class="modal-footer">
        <a class="waves-effect waves-green teal accent-4 btn" @click="vet" :disabled="loading">
          <loader v-if="loading" />
          <span v-else>Add</span>
        </a>
        <a class="modal-close left grey lighten-1 btn">Cancel</a>
      </section>
    </article>
  </main>
</template>

<script>
import axios from 'axios';
import Autocomplete from '@/components/Autocomplete.vue';
import Help from '@/components/Help.vue';
import LookAhead from '@/components/LookAhead.vue';
import Loader from '@/components/Loader.vue';

export default {
  name: 'Ratings',
  components: { Autocomplete, Help, LookAhead, Loader },
  props: {},
  data() {
    return {
      ratings: [],
      pointsChanged: 0,
      mostRecentlyAdded: 0,
      reversed: false,
      instruments: [],
      securityClasses: [],
      selectedSecurityClass: 1,
      selectedInstrument: {},
      usmTempModal: null,
      loading: false,
    };
  },
  computed: {
    analystId() {
      return Number(this.$auth.user.pk);
    },
    totalPoints() {
      return this.ratings.map(rating => rating.points).reduce((a, b) => a + b, 0);
    },
    instrumentPicks() {
      return this.instruments.map(instrument => `#${instrument.InstrumentId} ${instrument.Exchange}:${instrument.Symbol} ${instrument.Name}`);
    },
  },
  watch: {
    totalPoints() {
      this.mostRecentlyAdded = 0;
    },
    $route: 'fetchRatings',
  },
  methods: {
    handleAnalystAutocomplete(choice, elem, $event) {
      const analystId = choice.slice(choice.indexOf('#') + 1, choice.length);
      this.$router.push(`/analyst?analyst_id=${analystId}`);
    },
    async fetchRatings() {
      const { data } = await axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/ratings/query/?analyst_id=${this.analystId}`);
      this.ratings = data;
    },
    adjustedRating(rating, evt) {
      const points = Number(evt.target.value);
      this.pointsChanged = points - rating.points;
    },
    async updated(rating, evt) {
      const points = Number(evt.target.value);
      if (this.totalPoints + this.pointsChanged > 100) {
        return this.toast('Point allocation over limit', 'pink');
      }
      const updatedRating = this.ratings.filter(r => r.instrument.id === rating.instrument.id)[0];
      if (points !== updatedRating.points) {
        const { data } = await axios.post(`${this.$store.getters.VUE_APP_APING_URL}/insights/ratings/modify_rating/`, {
          method: 'update',
          email: this.$auth.user.email,
          instrument_pk: rating.instrument.id,
          points,
        });
        updatedRating.points = points;
        updatedRating.updated = new Date().toISOString();
        this.pointsChanged = 0;
        return this.toast(`Saved ${rating.instrument.exchange.quotes_symbol}:${rating.instrument.symbol}`, 'green');
      }
    },
    async confirmed(rating) {
      const { data } = await axios.post(`${this.$store.getters.VUE_APP_APING_URL}/insights/ratings/modify_rating/`, {
        method: 'confirm',
        email: this.$auth.user.email,
        instrument_pk: rating.instrument.id,
      });
      const confirmedRating = this.ratings.filter(r => r.instrument.id === rating.instrument.id)[0];
      confirmedRating.updated = new Date().toISOString();
      this.toast(`Confirmed ${rating.instrument.exchange.quotes_symbol}:${rating.instrument.symbol}`, 'green');
    },
    async deleted(rating) {
      const ticker = `${rating.instrument.exchange.quotes_symbol}:${rating.instrument.symbol}`;
      if (confirm(`Really, remove ${ticker}?`)) {
        const { data } = await axios.delete(`${this.$store.getters.VUE_APP_APING_URL}/insights/ratings/modify_rating/?method=delete&email=${this.$auth.user.email}&instrument_pk=${rating.instrument.id}`);
        this.toast(`Removed ${ticker}`, 'green');
        this.fetchRatings();
      }
    },
    async added(label, htmlElem) {
      const instrumentId = Number(label.slice(1, label.indexOf(' ')));
      this.selectedInstrument = await this.fetchInstrument(instrumentId);
      if (!this.selectedInstrument.synced_with_xpressfeed) {
        document.getElementById('ratings-quotes-lookahead').value = '';
        return this.usmTempModal.open();
      }
      const instrument = this.selectedInstrument;
      if (this.ratings.map(rating => rating.instrument.id).includes(instrument.id)) {
        return this.toast(`${instrument.ticker} is already in ratings`, 'pink');
      }
      if (instrument.inactive_date) {
        return this.toast(`${instrument.ticker} has been delisted in Fool IQ`, 'pink');
      }
      const vetted = await this.vetExisting(instrumentId);
      if (!vetted.new.iq_include) {
        const errorMessage = vetted.validation?.error_message
          || `${instrument.ticker} does not meet liquidity threshold in Fool IQ`;
        return this.toast(errorMessage, 'pink');
      }
      const { data } = await axios.put(`${this.$store.getters.VUE_APP_APING_URL}/insights/ratings/modify_rating/`, {
        method: 'create',
        email: this.$auth.user.email,
        instrument_pk: instrument.id,
        points: 0,
      });
      this.mostRecentlyAdded = instrumentId;
      this.fetchRatings();
      document.getElementById('ratings-quotes-lookahead').value = '';
      return this.toast(`Added ${label}!`, 'green');
    },
    sortRatings(path) {
      const args = path.split('.');
      let results = this.ratings.sort((a, b) => {
        let aa = a;
        let bb = b;
        args.forEach(arg => {
          aa = aa[arg];
          bb = bb[arg];
        });
        if (aa > bb) { return 1; }
        if (aa < bb) { return -1; }
        return 0;
      });
      if (this.reversed) {
        results = results.reverse();
      }
      this.reversed = !this.reversed;
      this.ratings = results;
    },
    async fetchInstruments(query) {
      const { data } = await axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/quotes/instruments/search/?q=${query}`);
      this.instruments = data.SearchResults;
    },
    async fetchInstrument(instrumentId) {
      const resp = await axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/instruments/equity/${instrumentId}/`);
      return resp.data[0];
    },
    async fetchCrypto(instrumentId) {
      const resp = await axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/instruments/crypto/${instrumentId}/`);
      return resp.data[0];
    },
    async fetchSecurityClasses() {
      const { data } = await axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/instruments/security_classes/`);
      this.securityClasses = data;
    },
    initModal() {
      this.usmTempModal = this.materialize.Modal.init(document.getElementById('ratings-usm-temp-modal'), {
        preventScrolling: false,
        dismissible: false,
      });
    },
    async vet() {
      if (this.selectedSecurityClass !== 1) {
        this.toast('Only "stocks" can be added to a scorecard rating', 'pink');
        return this.usmTempModal.close();
      }
      this.loading = true;
      const { data } = await axios.put(`${this.$store.getters.VUE_APP_APING_URL}/insights/instruments/vet/`, {
        analyst_id: this.analystId,
        instrument_id: this.selectedInstrument.id,
        security_class_id: this.selectedSecurityClass,
      });
      await this.added(`#${this.selectedInstrument.id} `);
      this.loading = false;
      this.usmTempModal.close();
    },
    async vetExisting(instrumentId) {
      const { data } = await axios.put(`${this.$store.getters.VUE_APP_APING_URL}/insights/instruments/vet/`, {
        analyst_id: this.analystId,
        instrument_id: instrumentId,
      });
      return data;
    },
  },
  mounted() {
    this.fetchRatings();
    this.fetchSecurityClasses();
    this.initModal();
  },
};
</script>

<style lang="scss" scoped>
@import '@/main.scss';

.collection {
  border: none;
}

.table-wrapper {
  border: 1px solid $tmf-gains;
  border-radius: 10px;
  padding: 12px 15px 12px 15px;
}
</style>
